import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import Cookies from "js-cookie";
import {
  MessageSquare,
  Plus,
  Trash2,
  ListOrdered,
  Tag,
  CloudRain,
  CheckCircle,
  AlertTriangle,
  X,
  Loader2,
} from "lucide-react";
import Sidebar from "@/components/Sidebar";
import DashboardFooter from "@/components/DashboardFooter";
import WarmUpTagPickerModal from "@/components/WarmUpTagPickerModal";
import {
  MESSAGE_TYPES,
  WEATHER_SEND_MODES,
  DELAY_ANCHORS,
  WEATHER_FORECAST_MERGE_TAG,
  createEmptyWarmUpMessage,
  getDelayAnchorMeta,
  normalizeWarmUpMessages,
  loadLegacyWarmUpDraftFromLocalStorage,
  clearWarmUpLocalStorageDraft,
  getWarmUpSequencesApiUrl,
} from "@/constants/warmUpSequenceDefaults";
import {
  WARM_UP_MERGE_TAGS,
  buildDynamicQualificationTags,
  mergeWarmUpTags,
  filterTagsByMessageType,
  filterTagsForWarmUpPicker,
} from "@/constants/warmUpMergeTags";
import { copyToClipboard } from "@/utils/copyToClipboard";
import { getClientAuthHeaders, getClientAuthToken } from "@/lib/apiAuth";

const COPY_NOTICE_MS = 3000;

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const SETTINGS_URL = `${BACKEND_URL}/api/bot/settings`;
const WARM_UP_SEQUENCES_URL = getWarmUpSequencesApiUrl(BACKEND_URL);

const ToggleSwitch = ({ enabled, onChange, testId }) => (
  <button
    type="button"
    role="switch"
    aria-checked={enabled}
    onClick={() => onChange(!enabled)}
    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-[#00D1FF]/50 ${
      enabled ? "bg-[#00D1FF]" : "bg-white/10"
    }`}
    data-testid={testId}
  >
    <span
      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow transition duration-200 ease-in-out ${
        enabled ? "translate-x-5" : "translate-x-0"
      }`}
    />
  </button>
);

const tagButtonClassName =
  "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-body text-[#00D1FF] border border-[#00D1FF]/30 bg-[#00D1FF]/10 hover:bg-[#00D1FF]/20 transition-colors";

const SequenceMessageCard = ({
  index,
  message,
  onChange,
  onRemove,
  testIdPrefix,
  onOpenTagPicker,
  onCopyWeatherForecastTag,
}) => {
  const [delayInput, setDelayInput] = useState(() => String(message.delay_days));

  useEffect(() => {
    setDelayInput(String(message.delay_days));
  }, [message.id, message.delay_days]);

  const handleTypeChange = (newType) => {
    const next = { ...message, message_type: newType };
    if (newType === "weather" && !next.weather_send_mode) {
      next.weather_send_mode = "regardless";
    }
    onChange(next);
  };

  const handleDelayChange = (e) => {
    const raw = e.target.value;
    if (raw === "") {
      setDelayInput("");
      onChange({ ...message, delay_days: 0 });
      return;
    }
    const digits = raw.replace(/\D/g, "");
    if (digits === "") return;
    const num = Math.max(0, parseInt(digits, 10));
    setDelayInput(String(num));
    onChange({ ...message, delay_days: num });
  };

  const handleDelayBlur = () => {
    if (delayInput === "") {
      setDelayInput("0");
      onChange({ ...message, delay_days: 0 });
    }
  };

  const handleDelayFocus = (e) => e.target.select();

  const delayAnchorMeta = getDelayAnchorMeta(message.delay_anchor);

  return (
    <div
      className={`p-4 rounded-lg bg-white/5 border border-white/10 space-y-4 transition-opacity ${
        message.enabled ? "" : "opacity-60"
      }`}
      data-testid={testIdPrefix}
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h3 className="text-[#F3F4F6] text-sm font-body font-semibold">
          Message #{index + 1}
        </h3>
        <div className="flex items-center gap-3 ml-auto">
          <span className="text-[#9CA3AF] text-xs font-body">Enabled</span>
          <ToggleSwitch
            enabled={!!message.enabled}
            onChange={(v) => onChange({ ...message, enabled: v })}
            testId={`${testIdPrefix}-enabled`}
          />
          <button
            type="button"
            onClick={onRemove}
            className="p-2 rounded-lg text-[#9CA3AF] hover:text-red-400 hover:bg-red-500/10 transition-colors"
            aria-label="Remove message"
            data-testid={`${testIdPrefix}-remove`}
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div>
        <label className="block text-[#9CA3AF] text-xs font-body mb-1">Message type</label>
        <select
          value={message.message_type}
          onChange={(e) => handleTypeChange(e.target.value)}
          className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-[#F3F4F6] text-sm font-body focus:outline-none focus:border-[#00D1FF]/50"
          data-testid={`${testIdPrefix}-type`}
        >
          {MESSAGE_TYPES.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </select>
      </div>

      <div className="space-y-3 p-3 rounded-lg bg-white/5 border border-white/10">
        <p className="text-[#F3F4F6] text-xs font-body font-semibold">Delay timing</p>
        <div className="space-y-2">
          {DELAY_ANCHORS.map((anchor) => (
            <label
              key={anchor.value}
              className="flex items-start gap-3 cursor-pointer text-sm font-body text-[#F3F4F6] hover:bg-white/5 rounded-lg p-2 -mx-2"
            >
              <input
                type="radio"
                name={`${testIdPrefix}-delay-anchor`}
                value={anchor.value}
                checked={message.delay_anchor === anchor.value}
                onChange={() => onChange({ ...message, delay_anchor: anchor.value })}
                className="mt-0.5 accent-[#00D1FF]"
                data-testid={
                  anchor.value === "before_appointment"
                    ? `${testIdPrefix}-delay-anchor-before`
                    : `${testIdPrefix}-delay-anchor-after`
                }
              />
              <span className="text-[#9CA3AF] leading-snug">{anchor.label}</span>
            </label>
          ))}
        </div>
        <div>
          <label className="block text-[#9CA3AF] text-xs font-body mb-1">
            {delayAnchorMeta.delayLabel}
          </label>
          <input
            type="text"
            inputMode="numeric"
            value={delayInput}
            onChange={handleDelayChange}
            onBlur={handleDelayBlur}
            onFocus={handleDelayFocus}
            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-[#F3F4F6] text-sm font-body focus:outline-none focus:border-[#00D1FF]/50"
            data-testid={`${testIdPrefix}-delay`}
          />
          <p className="text-[#9CA3AF] text-xs font-body mt-1 leading-snug">
            {delayAnchorMeta.helper(message.delay_days)}
          </p>
        </div>
      </div>

      <div className="flex items-start justify-between gap-3 p-3 rounded-lg bg-white/5 border border-white/10">
        <div className="min-w-0">
          <p className="text-[#F3F4F6] text-xs font-body font-semibold">AI corrected</p>
          <p className="text-[#9CA3AF] text-xs font-body mt-1 leading-snug">
            When enabled, the message text will be rewritten to sound more natural for each lead
            before sending.
          </p>
        </div>
        <ToggleSwitch
          enabled={!!message.ai_corrected}
          onChange={(v) => onChange({ ...message, ai_corrected: v })}
          testId={`${testIdPrefix}-ai-corrected`}
        />
      </div>

      {message.message_type === "weather" && (
        <div className="space-y-2 pt-1 border-t border-white/10">
          <p className="text-[#F3F4F6] text-xs font-body font-semibold uppercase tracking-wide pt-2">
            Weather delivery
          </p>
          <div className="space-y-2">
            {WEATHER_SEND_MODES.map((mode) => (
              <label
                key={mode.value}
                className="flex items-start gap-3 cursor-pointer text-sm font-body text-[#F3F4F6] hover:bg-white/5 rounded-lg p-2 -mx-2"
              >
                <input
                  type="radio"
                  name={`${testIdPrefix}-weather-mode`}
                  value={mode.value}
                  checked={message.weather_send_mode === mode.value}
                  onChange={() =>
                    onChange({ ...message, weather_send_mode: mode.value })
                  }
                  className="mt-0.5 accent-[#00D1FF]"
                  data-testid={
                    mode.value === "regardless"
                      ? `${testIdPrefix}-weather-regardless`
                      : `${testIdPrefix}-weather-non-ideal`
                  }
                />
                <span className="text-[#9CA3AF] leading-snug">{mode.label}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      <div>
        <div className="flex items-center justify-between gap-2 mb-1">
          <label className="text-[#9CA3AF] text-xs font-body">Message text</label>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            {message.message_type === "weather" && (
              <button
                type="button"
                onClick={onCopyWeatherForecastTag}
                className={tagButtonClassName}
                aria-label="Copy weather forecast tag"
                data-testid={`${testIdPrefix}-weather-forecast-tag`}
              >
                <CloudRain className="w-3.5 h-3.5" />
                Weather Forecast Tag
              </button>
            )}
            <button
              type="button"
              onClick={onOpenTagPicker}
              className={tagButtonClassName}
              aria-label="Add merge tag"
              data-testid={`${testIdPrefix}-add-tag`}
            >
              <Tag className="w-3.5 h-3.5" />
              Add tag
            </button>
          </div>
        </div>
        <textarea
          rows={4}
          value={message.text}
          onChange={(e) => onChange({ ...message, text: e.target.value })}
          placeholder="Message text for the lead…"
          className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-[#F3F4F6] text-sm font-body focus:outline-none focus:border-[#00D1FF]/50 resize-y min-h-[96px]"
          data-testid={`${testIdPrefix}-text`}
        />
      </div>
    </div>
  );
};

const WarmUpSequences = () => {
  const [messages, setMessages] = useState([createEmptyWarmUpMessage()]);
  const [sequencesLoading, setSequencesLoading] = useState(true);
  const [saveState, setSaveState] = useState("idle");
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [tagModalContext, setTagModalContext] = useState(null);
  const [dynamicQualTags, setDynamicQualTags] = useState([]);
  const [botSettings, setBotSettings] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [copyNotice, setCopyNotice] = useState(null);
  const noticeTimeoutRef = useRef(null);

  const clearCopyNoticeTimeout = useCallback(() => {
    if (noticeTimeoutRef.current) {
      clearTimeout(noticeTimeoutRef.current);
      noticeTimeoutRef.current = null;
    }
  }, []);

  useEffect(() => () => clearCopyNoticeTimeout(), [clearCopyNoticeTimeout]);

  const showCopyNotice = useCallback(
    (ok, successMessage) => {
      clearCopyNoticeTimeout();
      setCopyNotice(
        ok
          ? { type: "success", message: successMessage }
          : {
              type: "error",
              message: "Could not copy tag. Try again or copy manually.",
            }
      );
      noticeTimeoutRef.current = setTimeout(() => {
        setCopyNotice(null);
        noticeTimeoutRef.current = null;
      }, COPY_NOTICE_MS);
    },
    [clearCopyNoticeTimeout]
  );

  const handleCopyWeatherForecastTag = useCallback(async () => {
    const ok = await copyToClipboard(WEATHER_FORECAST_MERGE_TAG);
    showCopyNotice(ok, "Weather forecast tag copied to clipboard");
  }, [showCopyNotice]);

  const allMergeTags = useMemo(
    () => mergeWarmUpTags(WARM_UP_MERGE_TAGS, dynamicQualTags),
    [dynamicQualTags]
  );

  useEffect(() => {
    const token = getClientAuthToken();
    if (!token || !BACKEND_URL) {
      setSequencesLoading(false);
      setMessages([createEmptyWarmUpMessage()]);
      setIsDirty(false);
      return;
    }

    let cancelled = false;
    (async () => {
      setSequencesLoading(true);
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const res = await axios.get(WARM_UP_SEQUENCES_URL, { headers });
        if (cancelled) return;

        if (res.data?.has_sequences && res.data.sequences?.messages) {
          const loaded = normalizeWarmUpMessages(res.data.sequences.messages);
          setMessages(
            loaded.length > 0 ? loaded : [createEmptyWarmUpMessage()]
          );
          clearWarmUpLocalStorageDraft();
          setIsDirty(false);
          return;
        }

        const legacy = loadLegacyWarmUpDraftFromLocalStorage();
        if (legacy && legacy.length > 0) {
          await axios.put(WARM_UP_SEQUENCES_URL, { messages: legacy }, { headers });
          if (cancelled) return;
          setMessages(legacy);
          clearWarmUpLocalStorageDraft();
          setIsDirty(false);
          return;
        }

        setMessages([createEmptyWarmUpMessage()]);
        setIsDirty(false);
      } catch {
        if (!cancelled) {
          setMessages([createEmptyWarmUpMessage()]);
          setIsDirty(false);
        }
      } finally {
        if (!cancelled) setSequencesLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (saveState !== "saved") return undefined;
    const t = setTimeout(() => setSaveState("idle"), 2000);
    return () => clearTimeout(t);
  }, [saveState]);

  useEffect(() => {
    const token = getClientAuthToken();
    if (!token || !BACKEND_URL) {
      setSettingsLoading(false);
      setBotSettings(null);
      setDynamicQualTags([]);
      return;
    }

    let cancelled = false;
    (async () => {
      setSettingsLoading(true);
      try {
        const res = await axios.get(SETTINGS_URL, {
          headers: getClientAuthHeaders(),
        });
        if (cancelled) return;
        const settings = res.data?.settings ?? null;
        setBotSettings(settings);
        const questions = settings?.qualification_logic?.questions;
        setDynamicQualTags(buildDynamicQualificationTags(questions));
      } catch {
        if (!cancelled) {
          setBotSettings(null);
          setDynamicQualTags([]);
        }
      } finally {
        if (!cancelled) setSettingsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const markDirty = useCallback(() => {
    setIsDirty(true);
    setSaveState("idle");
  }, []);

  const handleSave = useCallback(async () => {
    const token = getClientAuthToken();
    if (!token || !BACKEND_URL) {
      setSaveState("error");
      return;
    }

    setIsSaving(true);
    setSaveState("idle");
    try {
      await axios.put(
        WARM_UP_SEQUENCES_URL,
        { messages: normalizeWarmUpMessages(messages) },
        { headers: getClientAuthHeaders() }
      );
      setIsDirty(false);
      setSaveState("saved");
    } catch {
      setSaveState("error");
    } finally {
      setIsSaving(false);
    }
  }, [messages]);

  const addMessage = useCallback(() => {
    markDirty();
    setMessages((prev) => [...prev, createEmptyWarmUpMessage()]);
  }, [markDirty]);

  const updateMessage = useCallback((index, value) => {
    markDirty();
    setMessages((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  }, [markDirty]);

  const removeMessage = useCallback((index) => {
    markDirty();
    setMessages((prev) => prev.filter((_, i) => i !== index));
    setTagModalContext((ctx) => {
      if (!ctx) return null;
      if (ctx.messageIndex === index) return null;
      if (ctx.messageIndex > index) {
        return { ...ctx, messageIndex: ctx.messageIndex - 1 };
      }
      return ctx;
    });
  }, [markDirty]);

  const openTagPicker = useCallback((messageIndex) => {
    const msg = messages[messageIndex];
    if (!msg) return;
    setTagModalContext({
      messageIndex,
      messageType: msg.message_type || "general",
    });
  }, [messages]);

  const modalMessageType = tagModalContext?.messageType ?? "general";
  const modalTags = useMemo(
    () =>
      filterTagsForWarmUpPicker(
        filterTagsByMessageType(allMergeTags, modalMessageType)
      ),
    [allMergeTags, modalMessageType]
  );

  return (
    <div className="min-h-screen bg-[#0F1115] flex" data-testid="warm-up-sequences-page">
      {copyNotice && (
        <div
          className="fixed top-6 right-6 z-[110] animate-in slide-in-from-top-2 duration-300"
          data-testid="warm-up-copy-toast"
          role="status"
        >
          <div
            className={`flex items-center gap-3 px-4 py-3 rounded-xl shadow-2xl border ${
              copyNotice.type === "success"
                ? "bg-[#01B47A]/10 border-[#01B47A]/30"
                : "bg-red-500/10 border-red-500/30"
            }`}
          >
            {copyNotice.type === "success" ? (
              <CheckCircle className="w-5 h-5 text-[#01B47A] flex-shrink-0" />
            ) : (
              <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0" />
            )}
            <span
              className={`text-sm font-medium font-body ${
                copyNotice.type === "success" ? "text-[#01B47A]" : "text-red-400"
              }`}
            >
              {copyNotice.message}
            </span>
            <button
              type="button"
              onClick={() => {
                clearCopyNoticeTimeout();
                setCopyNotice(null);
              }}
              className="ml-1 p-1 hover:bg-white/10 rounded-lg transition-all text-[#9CA3AF]"
              aria-label="Dismiss"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
      <Sidebar />
      <div className="flex-1 md:ml-64">
        <main className="pt-8 pb-16 px-6 max-w-3xl mx-auto">
          <div className="mb-8">
            <h1 className="font-heading font-bold text-2xl md:text-3xl text-[#F3F4F6] mb-2 flex items-center gap-3">
              <MessageSquare className="w-8 h-8 text-[#00D1FF]" />
              Warm-up Sequences
            </h1>
            <p className="text-[#9CA3AF] font-body text-sm leading-relaxed">
              These messages will be sent to qualified leads who have a calendar meeting
              scheduled. Messages are sent until the scheduled meeting time.
            </p>
          </div>

          <div className="glass rounded-2xl p-6 border border-white/10 space-y-6">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <h2 className="text-[#F3F4F6] font-heading font-semibold text-lg flex items-center gap-2">
                <ListOrdered className="w-5 h-5 text-[#00D1FF]" />
                Messages
              </h2>
              <div className="flex items-center gap-3 ml-auto">
                {!sequencesLoading && saveState === "saved" && (
                  <span className="text-[#01B47A] text-xs font-body">Saved</span>
                )}
                {!sequencesLoading && saveState === "error" && (
                  <span className="text-red-400 text-xs font-body">Could not save</span>
                )}
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={sequencesLoading || isSaving || !isDirty}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-heading font-bold transition-colors duration-300 ${
                    !sequencesLoading && !isSaving && isDirty
                      ? "bg-[#00D1FF] text-[#0F1115] hover:bg-[#00D1FF]/90"
                      : "bg-[#00D1FF]/50 text-[#0F1115]/70 cursor-not-allowed"
                  }`}
                  data-testid="warm-up-save-sequences"
                >
                  {isSaving ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    "Save"
                  )}
                </button>
                <button
                  type="button"
                  onClick={addMessage}
                  disabled={sequencesLoading}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#00D1FF]/10 border border-[#00D1FF]/30 text-[#00D1FF] text-sm font-body hover:bg-[#00D1FF]/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="warm-up-add-message"
                >
                  <Plus className="w-4 h-4" />
                  Add message
                </button>
              </div>
            </div>

            {sequencesLoading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-8 h-8 text-[#00D1FF] animate-spin" />
              </div>
            ) : messages.length === 0 ? (
              <p className="text-[#9CA3AF] text-sm font-body text-center py-8">
                No messages yet. Click Add message to create your first warm-up step.
              </p>
            ) : (
              <div className="space-y-4">
                {messages.map((msg, idx) => (
                  <SequenceMessageCard
                    key={msg.id}
                    index={idx}
                    message={msg}
                    onChange={(v) => updateMessage(idx, v)}
                    onRemove={() => removeMessage(idx)}
                    testIdPrefix={`warm-up-message-${idx}`}
                    onOpenTagPicker={() => openTagPicker(idx)}
                    onCopyWeatherForecastTag={handleCopyWeatherForecastTag}
                  />
                ))}
              </div>
            )}

            {!sequencesLoading && messages.length === 0 && (
              <div className="flex justify-center">
                <button
                  type="button"
                  onClick={addMessage}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-white/20 text-[#F3F4F6] text-sm font-body hover:bg-white/5 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Add message
                </button>
              </div>
            )}
          </div>
        </main>
        <DashboardFooter />
      </div>

      <WarmUpTagPickerModal
        open={tagModalContext !== null}
        onClose={() => setTagModalContext(null)}
        messageType={modalMessageType}
        tags={modalTags}
        botSettings={botSettings}
        settingsLoading={settingsLoading}
      />
    </div>
  );
};

export default WarmUpSequences;
